import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, getApiBaseUrl } from '@circuitry-ai/doc-data';
import { UXGenieTemplateRenderer } from './DynamicJSXRenderer';
import { ErrorBoundaryCatch } from '../utiluicomponents/ErrorBoundaryCatch';

/** Event the HTML template posts to the host — any payload shape. */
export type UxgenieTemplateEvent = {
    [key: string]: unknown;
};


export interface UxgenieTemplateContentViewProps {
    /** The template code to render — JSX by default, or a full standalone HTML document when `isHtml` is set (ignored when `htmlSrc` is also set) */
    code: string;
    /** The data to bind into the template (JSX mode) or post into the iframe (HTML mode) */
    data: any;
    /** Additional options data posted into the iframe (HTML mode) alongside `data` — not yet consumed by any template, reserved for future template requirements */
    options?: any;
    /** HTML-template events (iframe `postMessage` after origin/source checks), other than `domReady`. */
    onMessage?: (event: UxgenieTemplateEvent) => void;
    /**
     * Set when `code` (or `htmlSrc`) is a standalone HTML document (e.g. a saved UXGenie
     * template with `output_type: 'html'`) rather than a JSX snippet. Renders it in a
     * sandboxed iframe instead of transpiling it as JSX, and posts `data` into the iframe
     * via `postMessage` once it loads — standalone HTML templates render themselves from
     * data sent this way rather than data baked into the markup.
     */
    isHtml?: boolean;
    /**
     * A directly loadable URL (e.g. a presigned S3 URL) for the HTML document — the iframe
     * loads it via `src` instead of fetching the text and inlining it as `srcDoc`. Takes
     * precedence over `code` when `isHtml` is set.
     */
    htmlSrc?: string;
}

export interface HtmlTemplateFrameProps {
    /** Full standalone HTML document. Loaded via a same-origin blob URL. Ignored when `src` is set. */
    code: string;
    /** A directly loadable URL (e.g. a presigned S3 URL) — loaded via `src` instead of a blob URL built from `code`. */
    src?: string;
    /** Data posted into the iframe via `postMessage` once it loads (and re-posted if it changes). */
    data: any;
    /** Additional options data posted into the iframe alongside `data`, under an `options` key — not yet consumed by any template, reserved for future template requirements */
    options?: any;
    /** Overrides the iframe's default sizing classes. */
    className?: string;
    /** Message type the embedded template sends when it is ready. Defaults to `domReady`. */
    readyEvent?: string;
    /** Receives authenticated, source-checked messages other than the ready event. */
    onMessage?: (payload: any) => void;
}

/**
 * Renders a standalone HTML template in a sandboxed iframe — loaded directly via `src`
 * (e.g. a presigned URL) when given one, or via a same-origin blob URL built from `code`
 * otherwise — posting `data` into it via `postMessage` once the template signals it's
 * ready to receive it (and re-posting if `data` changes afterwards).
 *
 * `code` is loaded through a blob URL rather than the iframe's `srcDoc` attribute —
 * `srcDoc` is reserved for the UXGenie template editor's own live-editing preview
 * (`WorkflowHtmlPreview`), where the document legitimately regenerates on every
 * keystroke. Everywhere else, including here, a blob URL is a real (same-origin)
 * navigation the iframe can be measured and reasoned about consistently with a
 * presigned `src`.
 *
 * The template signals readiness itself by posting `{ type: 'domReady' }` up to this
 * frame (using its own `document.referrer` as the target origin) once its own
 * message-listener is registered. Waiting for that — rather than posting blindly after
 * the iframe's `load` event — avoids the race where `load` fires before the child's
 * listener is wired up (e.g. one registered on `DOMContentLoaded`) and the message is
 * silently dropped.
 *
 * Exported so other viewers that embed a template inside their own layout/modal chrome
 * (e.g. `ClaimScoreGeneratorRenderer`'s template preview modal) can reuse the same
 * HTML-loading + postMessage logic instead of duplicating it.
 */
export const HtmlTemplateFrame: React.FC<HtmlTemplateFrameProps> = ({
    code,
    src,
    data,
    options,
    className,
    readyEvent = 'domReady',
    onMessage,
}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [isDomReady, setIsDomReady] = useState(false);
    const [autoHeight, setAutoHeight] = useState<number | null>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);

    // The template needs to make its own authenticated calls back to our APIs, so the
    // auth token and org id are posted alongside the bound data rather than requiring
    // every caller to thread them through as props.
    const token = useSelector((state: RootState) => (state.auth as any)?.token ?? '');
    const authUserDetails = useSelector((state: RootState) => (state.auth as any)?.user?.details);
    const orgId = String(authUserDetails?.org_id ?? authUserDetails?.['https://circuitry.ai/org_id'] ?? '');
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;

    useEffect(() => {
        setIsDomReady(false);
        setAutoHeight(null);
    }, [code, src]);

    // Build a same-origin blob URL for `code` whenever there's no directly loadable
    // `src` — this is what stands in for `srcDoc` here (see the component doc above).
    useEffect(() => {
        if (src || !code) {
            setBlobUrl(null);
            return;
        }
        const url = URL.createObjectURL(new Blob([code], { type: 'text/html' }));
        setBlobUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [code, src]);

    const effectiveSrc = src ?? blobUrl ?? undefined;
    // Only a caller-provided `src` (e.g. a presigned S3 URL) is genuinely cross-origin
    // and unmeasurable — our own blob URL resolves to this page's own origin.
    const isExternalSrc = Boolean(src);

    // Size the iframe to its actual document height instead of a fixed min-height, so a
    // shorter template doesn't leave dead space below it, and a taller one doesn't get
    // its own nested scrollbar. Only possible for our own same-origin blob URL — a
    // directly loaded external `src` (e.g. a presigned S3 URL) is cross-origin and can't
    // be measured. Gated on `isDomReady` (the template's own `domReady` signal) rather
    // than the iframe's `load` event — a `ResizeObserver` picks up any further growth as
    // the page finishes rendering, so an early-but-readable document is enough to start
    // measuring.
    useEffect(() => {
        if (!isDomReady || isExternalSrc) return;
        let doc: Document | undefined;
        try {
            doc = iframeRef.current?.contentWindow?.document;
        } catch {
            return;
        }
        if (!doc?.documentElement) return;

        const measure = () => {
            const height = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight || 0;
            if (height > 0) setAutoHeight(height);
        };
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(doc.documentElement);
        return () => observer.disconnect();
    }, [isDomReady, isExternalSrc]);

    // Never target '*' — that would hand the (potentially sensitive, e.g. claim)
    // payload to whatever origin the frame happens to be showing, including one it may
    // have since navigated to. Restrict delivery to the origin we actually intended:
    // the presigned URL's own origin when loaded directly via `src`, or this page's own
    // origin for our own blob URL (a blob URL's `origin` resolves to its creator's).
    const resolveTargetOrigin = (): string | null => {
        try {
            return effectiveSrc ? new URL(effectiveSrc, window.location.href).origin : window.location.origin;
        } catch {
            return null;
        }
    };

    const postDataToIframe = useCallback(() => {
        const isEmptyValue = (value: any) => value == null || value === '';
        if (isEmptyValue(data) && isEmptyValue(options)) return;
        let payload = data;
        if (typeof payload === 'string' && payload !== '') {
            try {
                payload = JSON.parse(payload);
            } catch {
                return;
            }
        }
        const targetOrigin = resolveTargetOrigin();
        if (!targetOrigin) return;
        const isPlainObject = payload != null && typeof payload === 'object' && !Array.isArray(payload);
        const baseUrl = getApiBaseUrl();
        const message = isPlainObject
            ? { ...payload, options, eventType: 'templateData', token, org_id: orgId, base_url: baseUrl }
            : { data: payload, options, eventType: 'templateData', token, org_id: orgId, base_url: baseUrl };
        console.debug(`posting data to iframe at ${targetOrigin}`, message);
        iframeRef.current?.contentWindow?.postMessage(message, targetOrigin);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, options, effectiveSrc, token, orgId]);

    // Re-posts when `data`/`options` change *after* the template is already ready.
    // The initial post (isDomReady flipping false -> true) is owned by the `domReady`
    // message handler below, not here — otherwise both would fire for the same
    // transition and the template would receive the same message twice.
    const isDomReadyRef = useRef(isDomReady);
    useEffect(() => {
        if (isDomReadyRef.current && isDomReady) postDataToIframe();
        isDomReadyRef.current = isDomReady;
    }, [isDomReady, postDataToIframe]);

    // Listen for the template's own `domReady` signal rather than assuming readiness
    // from the iframe's `load` event.
    //
    // Anything that reloads this iframe out from under us without React knowing (e.g.
    // the docked PAL chatbot widget used to reparent the host page's DOM on every
    // open/close, which forces any nested iframe to reload — fixed at the source in
    // PAL's own dock implementation, but nothing stops a host page or extension from
    // doing something similar in the future) leaves `isDomReady` stuck at `true`, so
    // the effect above wouldn't fire again when the template re-announces `domReady`
    // after reloading — it would sit on its own fallback/sample content forever.
    // Re-post unconditionally on every accepted `domReady`, not just the first one, so
    // a reload like that still ends up with the real data. This is the sole poster for
    // both the initial ready and any subsequent reload.
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const payload = event.data;
            if (!payload || typeof payload !== 'object') return;

            const iframeWindow = iframeRef.current?.contentWindow;
            if (iframeWindow && event.source && event.source !== iframeWindow) return;

            const expectedOrigin = resolveTargetOrigin();
            if (!expectedOrigin || (event.origin && event.origin !== expectedOrigin)) return;

            if (payload.type === readyEvent) {
                setIsDomReady(true);
                postDataToIframe();
                return;
            }
            onMessageRef.current?.(payload);
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveSrc, postDataToIframe, readyEvent]);

    if (!src && !code) {
        return (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center text-sm text-gray-600">
                No template content available
            </div>
        );
    }

    return (
        <iframe
            ref={iframeRef}
            title="UXGenie HTML template"
            src={effectiveSrc}
            // Templates open presigned document URLs with `window.open(url, '_blank')`, which a
            // sandboxed frame blocks without `allow-popups`. The popup also has to escape the
            // sandbox, otherwise it inherits these flags and the browser refuses to render the
            // PDF in it; `allow-downloads` covers templates that save the file instead.
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
            allow="camera; microphone; fullscreen"
            // The generous min-height fallback only matters while unmeasured (or when
            // an external `src` rules measurement out entirely) — once `autoHeight` is
            // known, drop it so the measured height isn't clamped back up by it. Only
            // applies to our own default (no explicit `className` passed); a caller
            // that supplies its own className (e.g. a min/max-height it wants enforced
            // even post-measurement) keeps that behavior unchanged.
            className={
                className ??
                (autoHeight ? "w-full border-0 bg-white" : "w-full min-h-[calc(100vh-25px)] border-0 bg-white")
            }
            style={autoHeight ? { height: `${autoHeight}px` } : undefined}
        />
    );
};

/**
 * Common, header-less UXGenie template view: renders only the template content
 * bound to its data — no breadcrumb, share button, voice capture, or footer.
 * Reusable anywhere only the template content itself needs to be shown.
 */
export const UxgenieTemplateContentView: React.FC<UxgenieTemplateContentViewProps> = ({
    code,
    data,
    options,
    isHtml = false,
    htmlSrc,
    onMessage,
}) => (
    <div className="bg-gray-50 p-2">
        <div className="mx-auto bg-white shadow-sm">
            {isHtml ? (
                <HtmlTemplateFrame
                    code={code}
                    src={htmlSrc}
                    data={data}
                    options={options}
                    onMessage={onMessage}
                />
            ) : (
                // Contains template runtime errors to the template area instead of the whole page;
                // keyed on code so a corrected template gets a fresh boundary.
                <ErrorBoundaryCatch
                    key={code}
                    fallback={
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                            Could not render the template with this data.
                        </div>
                    }
                >
                    <UXGenieTemplateRenderer code={code} data={data} />
                </ErrorBoundaryCatch>
            )}
        </div>
    </div>
);

export default UxgenieTemplateContentView;
